<script lang="ts">
  import { getContext, onMount } from 'svelte';
  import { Link, type LinkProps } from '@squareup/public-web-design-system';
  import LinkComponentAnchor from './LinkComponent--anchor.svelte';
  import LinkComponentButton from './LinkComponent--button.svelte';
  import LinkComponentVideo from './LinkComponent--video.svelte';
  import LinkComponentIcon from './LinkComponent--icon.svelte';
  import LinkComponentText from './LinkComponent--text.svelte';
  import ModalPartial from '$components/common/partials/ModalPartials/ModalPartial.svelte';
  import { appendAvtToUrl } from '$lib/helpers/browser/turtl-link-tracking-helper';
  import { TURTL_HOST_NAME } from '$lib/constants/turtl-constants';
  import { DesignSystem as DesignSystemEnum } from '$lib/helpers/design-system';
  import type { LinkComponentProps } from './types';
  import type { SvelteComponent } from 'svelte';

  export let entryId: LinkComponentProps['entryId'];
  export let type: LinkComponentProps['type'];
  export let size: LinkComponentProps['size'];
  export let text: LinkComponentProps['text'];
  export let url: LinkComponentProps['url'];
  export let altText: LinkComponentProps['altText'];
  export let target: LinkComponentProps['target'] = undefined;
  export let nofollow: LinkComponentProps['nofollow'] = undefined;
  export let icon: LinkComponentProps['icon'] = undefined;
  export let videoPlayButtonSize: LinkComponentProps['videoPlayButtonSize'];
  export let videoPlayButtonTitle: LinkComponentProps['videoPlayButtonTitle'] = undefined;
  export let body: LinkComponentProps['body'] = undefined;
  export let balanceText: LinkComponentProps['balanceText'] = undefined;
  export let doArrowWrap: LinkComponentProps['doArrowWrap'] = undefined;
  export let textExceptLastWord: LinkComponentProps['textExceptLastWord'] = undefined;
  export let textLastWord: LinkComponentProps['textLastWord'] = undefined;
  export let scrollOffset: LinkComponentProps['scrollOffset'];
  // pass through props, currently only used to support no arrow links on StickyBannerSection
  export let noArrow: LinkComponentProps['noArrow'] = undefined;
  export let customCssClass: LinkComponentProps['customCssClass'];
  export let modal: LinkComponentProps['modal'] = undefined;
  export let isActive: LinkComponentProps['isActive'];

  const designSystem = getContext<DesignSystemEnum>('designSystem');

  const modalPartialConfig = modal ? modal.props.modalPartialConfig : {};
  let isModalOpen = false;

  function handleOnClick() {
    if (modal) {
      isModalOpen = true;
    }
  }

  function handleModalClose() {
    isModalOpen = false;
  }

  // casting modal as SvelteComponent<any, any, any> | undefined to avoid type error
  const transformModal = modal as SvelteComponent<any, any, any> | undefined;

  onMount(() => {
    // https://block.atlassian.net/browse/PWB-548
    // Append _avt to link href if it's a turtl link
    if (url.includes(TURTL_HOST_NAME)) {
      url = appendAvtToUrl(url);
    }
  });

  // There still needs to be a check here even though changing button and button-alt from a large size to a regular size
  // happens in the view model because of the types
  const designSystemLinkSizes: LinkProps['size'][] = ['small', 'regular'];
  let designSystemLinkSize = size as LinkProps['size'];

  if ((type === 'button' || type === 'button-alt') && !designSystemLinkSizes.includes(size)) {
    designSystemLinkSize = 'regular';
  }
</script>

<div
  class="LinkComponent LinkComponent-{entryId}-box-properties {customCssClass}"
  data-entry-id={entryId}
  on:click={handleOnClick}
>
  {#if type === 'button' || type === 'button-alt'}
    {#if designSystem === DesignSystemEnum.PublicWebDesignSystem}
      <Link
        variant={type}
        size={designSystemLinkSize}
        href={url}
        {altText}
        target={target ?? undefined}
        rel={nofollow ?? undefined}
        modal={transformModal}
        {text}
      />
    {:else}
      <LinkComponentButton {text} {url} target={target ?? undefined} {nofollow} {modal} variant={type} />
    {/if}
  {:else if type === 'video'}
    <LinkComponentVideo
      {size}
      {text}
      {url}
      target={target ?? undefined}
      {nofollow}
      {videoPlayButtonSize}
      {videoPlayButtonTitle}
      {doArrowWrap}
      {balanceText}
    />
  {:else if type === 'icon'}
    <LinkComponentIcon {text} {url} {target} {nofollow} {icon} {modal} />
  {:else if type === 'anchor'}
    <LinkComponentAnchor
      {text}
      {url}
      {scrollOffset}
      {textExceptLastWord}
      {textLastWord}
      locale={$$props.locale}
      {isActive}
    />
  {:else if designSystem === DesignSystemEnum.PublicWebDesignSystem && type === 'text'}
    <Link
      variant={type}
      {size}
      href={url}
      target={target ?? undefined}
      rel={nofollow ?? undefined}
      balanced={balanceText}
      {noArrow}
      modal={transformModal}
      {text}
      preventOrphanIcon={doArrowWrap}
    />
  {:else}
    <LinkComponentText
      {doArrowWrap}
      {text}
      {textExceptLastWord}
      {textLastWord}
      {balanceText}
      {url}
      {target}
      {nofollow}
      {body}
      {noArrow}
      {type}
      {modal}
      variant={type === 'text - plain' || type === 'text - reduced-motion' ? 'text' : type}
    />
  {/if}
  {#if modal && type !== 'video' && type !== 'anchor'}
    <ModalPartial
      on:closeModal={handleModalClose}
      modalPartialConfig={{ ...modalPartialConfig, active: isModalOpen }}
    />
  {/if}
</div>
